import { FGListen, FGTextInput, FieldGroup, showSuccess, showError, FGCheckboxInput } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import styled from "styled-components";
import { PersonApi } from "../../../../../api";
import { SharepointButton, SmallFormGenerator } from "../../../../../components";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { VeterinaryClinicsList } from "./VeterinaryClinicsList";

export interface IPersonVetPageProps {
  personId: number;
}
const StyledContainer = styled.div`
  & > * {
    margin-top: 2rem;
  }
`;

export const PersonVetPage: React.FunctionComponent<IPersonVetPageProps> = ({ personId }) => {
  const history = useHistory();
  const api = useApiService(PersonApi);
  const { t } = useTl();
  const { state } = useParams<{ state: string }>();

  const [vet, setVet] = React.useState<boolean>(false);
  const [vetId, setVetId] = React.useState<number>();
  const [vetRequest, setVetRequest] = React.useState<boolean>(true);

  const { data, saveItem, saving, loading } = useCrudApi(
    {
      getApiFn: () => api.getVeterinaryInformation(personId),
      saveApiFn: (d) => save(d),
      deleteApiFn: (d) => {},
    },
    [personId]
  );
  async function save(d) {
    try {
      const id = await api.saveVeterinaryInformation(d);
      showSuccess(t(ETLCodes.SaveSuccess));
      if (id > 0) {
        setVetId(id);
        setVet(true);
      }
      return id;
    } catch {
      showError(t(ETLCodes.Error));
    }
  }

  React.useEffect(() => {
    if (data && vetRequest) {
      setVet(data.isVet);
      setVetRequest(false);
      setVetId(data.veterinaryId);
    }
  }, [data, vetRequest]);

  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.personSharepointUri, [data?.personSharepointUri]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.personSharepointUri) {
      window.open(data?.personSharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await api.createInSharepoint(personId);
        history.push(`${ERoutes.people}/${+personId}/veterinary/edit`);
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.personSharepointUri, api, personId, history]);

  return (
    <>
      <SmallFormGenerator
        onSubmit={saveItem}
        initialValues={data}
        editMode={state === "edit"}
        minLabelWidth={150}
        saving={saving}
        loading={loading}
        showSaveEmptyButtonLabel={vet ? null : t(ETLCodes.CreateEmptyVet)}
      >
        <FGListen
          field="veterinaryNumber"
          onChanged={(value) => {
            if (data && data.veterinaryId > 0) {
              setVetId(data.veterinaryId);
              setVet(true);
            } else {
              setVet(false);
            }
          }}
        />
        <FieldGroup
          fieldsetProps={{
            title: t(ETLCodes.Veterinary),
            rightElement: vet && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            ),
          }}
        >
          <FGTextInput name="veterinaryNumber" label={t(ETLCodes.VeterinaryNumber)} />
          <FGCheckboxInput name="isActive" label={t(ETLCodes.Active)} />
        </FieldGroup>
      </SmallFormGenerator>

      {vet === true && (
        <StyledContainer>
          <VeterinaryClinicsList vetId={vetId} editMode={state === "edit"} />
        </StyledContainer>
      )}
    </>
  );
};
